import { useStytch, useStytchSession } from "@stytch/react";
import { getClientKey } from "./queries/user/useUserStats";
import { checkResponseStatus } from "@/lib/utils/responseChecker";
import i18n from "i18next";

// Unified request options for API methods in this module
export type AuthenticatedRequestOptions = {
  headersOpts?: HeadersInit;
  enableClientKey?: boolean;
  signal?: AbortSignal;
};

export const useAuthenticatedApiRequest = () => {

  const stytch = useStytch();
  const { session, fromCache } = useStytchSession();

  const buildHeaders = (
    { headersOpts = {}, enableClientKey = false }: Pick<AuthenticatedRequestOptions, "headersOpts" | "enableClientKey"> = {}
  ) => {
    let headers: HeadersInit = {};

    // Add auth header if session exists and is not from cache
    if (session && !fromCache) {
      const tokens = stytch.session.getTokens();
      if (tokens?.session_jwt) {
        headers.Authorization = `Bearer ${tokens.session_jwt}`;
      }
    }

    // Add client key header if enabled
    let clientKeyHeader: { clientKey?: string } = {};
    if (enableClientKey) {
      clientKeyHeader = { "clientKey": getClientKey() };
    }

    // Merge all headers with proper precedence
    if (headersOpts) {
      headers = {
        ...headersOpts,
        ...headers,
        ...clientKeyHeader,
      };
    } else {
      headers = {
        ...headers,
        ...clientKeyHeader,
      };
    }

    return headers;
  };

  // Derive a context slug from URL path (first segment after leading slash)
  const deriveContext = (url: URL) => {
    const parts = url.pathname.replace(/^\/+/, "").split("/");
    return parts[0] ? parts[0] : "general";
  };

  const get = async (
    url: URL,
    { headersOpts, enableClientKey, signal }: AuthenticatedRequestOptions = {}
  ) => {
    const headers = buildHeaders({ headersOpts, enableClientKey });
    const response = await fetch(url, { headers, signal });
    checkResponseStatus(
      response,
      i18n.getFixedT?.(null, ["errors"]) || (i18n.t.bind(i18n) as any),
      { context: deriveContext(url) }
    );
    return response;
  };

  const post = async (
    url: URL,
    body: string,
    { headersOpts, enableClientKey, signal }: AuthenticatedRequestOptions = {}
  ) => {
    const headers = buildHeaders({ headersOpts, enableClientKey });
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...headers },
      body,
      signal,
    });
    checkResponseStatus(
      response,
      i18n.getFixedT?.(null, ["errors"]) || (i18n.t.bind(i18n) as any),
      { context: deriveContext(url) }
    );
    return response;
  };

  const del = async (
    url: URL,
    { headersOpts, enableClientKey, signal }: AuthenticatedRequestOptions = {}
  ) => {
    const headers = buildHeaders({ headersOpts, enableClientKey });
    const response = await fetch(url, {
      method: 'DELETE',
      headers,
      signal,
    });
    checkResponseStatus(
      response,
      i18n.getFixedT?.(null, ["errors"]) || (i18n.t.bind(i18n) as any),
      { context: deriveContext(url) }
    );
    return response;
  };

  const patch = async (
    url: URL,
    body: string,
    { headersOpts, enableClientKey, signal }: AuthenticatedRequestOptions = {}
  ) => {
    const headers = buildHeaders({ headersOpts, enableClientKey });
    const response = await fetch(url, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...headers },
      body,
      signal,
    });
    checkResponseStatus(
      response,
      i18n.getFixedT?.(null, ["errors"]) || (i18n.t.bind(i18n) as any),
      { context: deriveContext(url) }
    );
    return response;
  };

  return { get, post, del, patch };
};
export default useAuthenticatedApiRequest;